import { DefaultValue, selector, selectorFamily } from 'recoil';
import type { Layout } from 'react-grid-layout';
import { studioBlockCustomWidth } from '../grid';
import { type BlockId, type CustomViewOptions, type GlobalCustomViewOptions, UnitFormat } from '../types';
import {
  allocatorAnalysisSubject,
  openAllocatorSubject,
  openAllocatorSubjectConfig,
  openPrivateAllocatorConfig,
} from './allocator';
import { isReportState } from './view';
import { blockAllFactorsSelected } from './blockConfig';
import { blockCustomMetricSettingsState, blockRowDisplayLimit } from './blockSettings';
import {
  studioCustomFooterText,
  studioHideFooterDisclaimer,
  studioHideFooterPagination,
  studioHideHeaderCompanyName,
  studioHideHeaderLogo,
  studioHideHeaderViewName,
  studioTitlePageHideCompanyLogo,
  studioTitlePageHasDisclosureTextAlignedRight,
  studioTitlePageHasFullPageBackgroundImage,
  studioSectionPageHasFullPageBackgroundImage,
  studioSectionPageHideCompanyName,
  viewPages,
} from './print';
import {
  blockContinuationPageCount,
  blockExpandedState,
  blockOriginalLayoutState,
  blockPersistedContinuationPageCountState,
} from '../../report-lab/blockExpansion';
import { continuationUserBlocksState, makeContinuationLayoutId } from '../../report-lab';
import {
  blockPrivateAssetAllocation,
  blockPrivateAssetIsCumulative,
  blockPrivateDefaultAsOfDateBehavior,
  blockStartingNAV,
  DEFAULT_CAPITAL_COMMITMENT_ALLOCATION,
} from './privateAssets';
import { viewSectionName, viewSubtitle, viewTitle } from './viewOptions';
import { customField_gridData } from './customMetrics';
import { blockSelectedPeerGroupId } from './peerGroups';
import { allBlockFontsState, blockTableSpacingRatioState } from '../fonts/blockFonts';
import {
  blockHoldingsCategoryIds,
  blockScenarios,
  blockYAxisUnitFormat,
  canToggleHideBlockBorders,
  correlationLabelDisplayType,
  geoLevelFilterType,
  hideBlockBorders,
  portfolioStrategyFilterType,
  sectionPageBackgroundImage,
  sectionPageBackgroundImages,
  sectorLevelFilterType,
  showBlockChangesColumn,
  viewCoverPageImage,
  viewCustomNotablePeriods,
  viewDataGridSizeType,
  viewImageId,
  viewImages,
  viewPrimaryColor,
  viewRichText,
  viewSectionTextColor,
  viewSelectedInvestmentGroupInformation,
  viewSelectedNotablePeriods,
  viewShowVenncast,
  viewTaxRatesPerSubject,
  multiLevelPortfolioStrategyFilterType,
  showBlockGainLoss,
  showBlockNetTaxImpact,
  blockScatterAxisConfig,
  pieChartLegendPosition,
} from './customViewOptions';
import { CURRENT_AS_OF_DATE, DEFAULT_PRIVATE_ASSET_ALLOCATION } from 'venn-utils';
import { isArray, isNil } from 'lodash';
import { blockAsOfDateSetting } from './historicalAssets';

export const customViewOptions = selectorFamily<CustomViewOptions, BlockId>({
  key: 'customViewOptions',
  get:
    (id) =>
    ({ get }) => {
      const selectedNotablePeriods = get(viewSelectedNotablePeriods(id));
      const selectedInvestmentGroupInformation = get(viewSelectedInvestmentGroupInformation(id));
      const customNotablePeriods = get(viewCustomNotablePeriods(id));
      const dataGridSizeType = get(viewDataGridSizeType(id));
      const portfolioStrategyFilter = get(portfolioStrategyFilterType(id));
      const multiLevelPortfolioStrategyFilterAtom = get(multiLevelPortfolioStrategyFilterType(id));
      const multiLevelPortfolioStrategyFilter = multiLevelPortfolioStrategyFilterAtom
        ? { ...multiLevelPortfolioStrategyFilterAtom, maxTruncatedLevel: undefined }
        : undefined;
      const geoLevelFilter = get(geoLevelFilterType(id));
      const sectorLevelFilter = get(sectorLevelFilterType(id));
      const BLOCK_WIDTH = get(studioBlockCustomWidth(id));
      const title = get(viewTitle(id));
      const sectionName = get(viewSectionName(id));
      const subtitle = get(viewSubtitle(id));
      const richText = get(viewRichText(id));
      const showVenncast = get(viewShowVenncast(id));
      const imageId = get(viewImageId(id));
      const scenarios = get(blockScenarios(id));
      const allFactorsSelected = get(blockAllFactorsSelected(id));
      const holdingsCategoryIds = get(blockHoldingsCategoryIds(id));
      const privateAssetIsCumulative = get(blockPrivateAssetIsCumulative(id));
      const privateDefaultAsOfDateBehavior = get(blockPrivateDefaultAsOfDateBehavior(id));
      const startingNAV = get(blockStartingNAV(id));
      const privateAssetAllocation = get(blockPrivateAssetAllocation(id));
      const customMetricsData = get(customField_gridData(id));
      const selectedPeerGroupId = get(blockSelectedPeerGroupId(id));
      const customMetricsSettings = get(blockCustomMetricSettingsState(id));
      const customFonts = get(allBlockFontsState(id));
      const tableSpacingRatio = get(blockTableSpacingRatioState(id));
      const yAxisUnitFormat = get(blockYAxisUnitFormat(id));
      // Don't store the hide borders setting if the block doesn't support block borders. This way we aren't locking blocks into some future behavior.
      // Also this prevents issues from settings being stored before the FF is enabled.
      const hideBorders = get(canToggleHideBlockBorders(id)) ? get(hideBlockBorders(id)) : undefined;
      const historicalPortfolioAsOfSetting = get(blockAsOfDateSetting(id));
      const showChangesColumn = get(showBlockChangesColumn(id));
      const correlationLabelType = get(correlationLabelDisplayType(id));
      const rowDisplayLimit = get(blockRowDisplayLimit(id));
      const showGainLoss = get(showBlockGainLoss(id));
      const showNetTaxImpact = get(showBlockNetTaxImpact(id));
      const scatterAxisConfig = get(blockScatterAxisConfig(id));
      const pieChartLegendPositionValue = get(pieChartLegendPosition(id));

      const expanded = get(blockExpandedState(id));
      const originalLayout = get(blockOriginalLayoutState(id));
      const expandedOriginalLayout = expanded && originalLayout ? originalLayout : undefined;

      const continuationCount = expanded ? get(blockContinuationPageCount(id)) : 0;
      const expandedContinuationPageCount = expanded && continuationCount > 0 ? continuationCount : undefined;

      let continuationUserBlocksPerBlock: Record<number, Layout> | undefined;
      if (expanded) {
        for (let c = 0; c < continuationCount; c++) {
          const userBlocks = get(
            continuationUserBlocksState(makeContinuationLayoutId({ sourceBlockId: id, continuationIndex: c })),
          );
          if (userBlocks.length > 0) {
            (continuationUserBlocksPerBlock ??= {})[c] = userBlocks;
          }
        }
      }

      const baseOptions = {
        selectedNotablePeriods,
        selectedInvestmentGroupInformation,
        customNotablePeriods,
        dataGridSizeType,
        portfolioStrategyFilter,
        multiLevelPortfolioStrategyFilter,
        geoLevelFilter,
        sectorLevelFilter,
        BLOCK_WIDTH,
        title,
        sectionName,
        subtitle,
        richText,
        showVenncast,
        imageId,
        scenarios,
        allFactorsSelected,
        holdingsCategoryIds,
        privateAssetIsCumulative,
        privateDefaultAsOfDateBehavior,
        startingNAV,
        privateAssetAllocation,
        yAxisUnitFormat,
        customMetricGrid: customMetricsData,
        selectedPeerGroupId,
        customMetricsSettings,
        historicalPortfolioAsOfDate: historicalPortfolioAsOfSetting,
        showChangesColumn,
        correlationLabelDisplayType: correlationLabelType,
        rowDisplayLimit,
        showGainLoss,
        showNetTaxImpact,
        scatterAxisConfig,
        pieChartLegendPosition: pieChartLegendPositionValue,
        expandedOriginalLayout,
        expandedContinuationPageCount,
        continuationUserBlocks: continuationUserBlocksPerBlock,
      };

      return get(isReportState)
        ? {
            ...baseOptions,
            customFonts,
            tableSpacingRatio,
            hideBorders,
          }
        : baseOptions;
    },
  set:
    (id) =>
    ({ set }, value) => {
      if (value instanceof DefaultValue) {
        return;
      }
      set(viewSelectedNotablePeriods(id), value.selectedNotablePeriods);
      set(viewSelectedInvestmentGroupInformation(id), value.selectedInvestmentGroupInformation);
      set(viewCustomNotablePeriods(id), value.customNotablePeriods);
      set(viewDataGridSizeType(id), value.dataGridSizeType);
      set(portfolioStrategyFilterType(id), value.portfolioStrategyFilter);
      set(multiLevelPortfolioStrategyFilterType(id), value.multiLevelPortfolioStrategyFilter);
      set(geoLevelFilterType(id), value.geoLevelFilter);
      set(sectorLevelFilterType(id), value.sectorLevelFilter);
      value.BLOCK_WIDTH && set(studioBlockCustomWidth(id), value.BLOCK_WIDTH);
      set(viewTitle(id), value.title);
      set(viewSectionName(id), value.sectionName);
      set(viewSubtitle(id), value.subtitle);
      set(viewRichText(id), value.richText);
      set(viewShowVenncast(id), value.showVenncast);
      set(viewImageId(id), value.imageId);
      value.scenarios && set(blockScenarios(id), value.scenarios);
      value.allFactorsSelected && set(blockAllFactorsSelected(id), value.allFactorsSelected);
      set(blockHoldingsCategoryIds(id), value.holdingsCategoryIds);
      set(blockPrivateAssetIsCumulative(id), value.privateAssetIsCumulative);
      value.privateDefaultAsOfDateBehavior != null &&
        set(blockPrivateDefaultAsOfDateBehavior(id), value.privateDefaultAsOfDateBehavior);
      set(blockStartingNAV(id), value.startingNAV ?? DEFAULT_CAPITAL_COMMITMENT_ALLOCATION);
      set(blockPrivateAssetAllocation(id), value.privateAssetAllocation ?? DEFAULT_PRIVATE_ASSET_ALLOCATION);
      value.customMetricGrid && set(customField_gridData(id), value.customMetricGrid);
      set(blockSelectedPeerGroupId(id), value.selectedPeerGroupId);
      value.customMetricsSettings && set(blockCustomMetricSettingsState(id), value.customMetricsSettings);
      value.customFonts && set(allBlockFontsState(id), value.customFonts);
      value.tableSpacingRatio && set(blockTableSpacingRatioState(id), value.tableSpacingRatio);
      set(blockYAxisUnitFormat(id), value.yAxisUnitFormat ?? UnitFormat.PERCENT);
      set(showBlockChangesColumn(id), !!value.showChangesColumn);
      set(correlationLabelDisplayType(id), value.correlationLabelDisplayType);
      set(blockRowDisplayLimit(id), value.rowDisplayLimit);
      set(showBlockGainLoss(id), !!value.showGainLoss);
      set(showBlockNetTaxImpact(id), !!value.showNetTaxImpact);
      set(blockScatterAxisConfig(id), value.scatterAxisConfig);
      if (!isNil(value.hideBorders)) {
        set(hideBlockBorders(id), value.hideBorders);
      }
      if (value.historicalPortfolioAsOfDate !== undefined) {
        if (!isArray(value.historicalPortfolioAsOfDate)) {
          // Previously as of date was stored as a single value, so we need to convert it to a tuple
          set(blockAsOfDateSetting(id), [value.historicalPortfolioAsOfDate, CURRENT_AS_OF_DATE]);
        } else {
          set(blockAsOfDateSetting(id), value.historicalPortfolioAsOfDate);
        }
      }
      set(pieChartLegendPosition(id), value.pieChartLegendPosition);
      if (value.expandedOriginalLayout) {
        set(blockExpandedState(id), true);
        set(blockOriginalLayoutState(id), value.expandedOriginalLayout);
      }
      if (value.expandedContinuationPageCount != null) {
        set(blockPersistedContinuationPageCountState(id), value.expandedContinuationPageCount);
      }
      if (value.continuationUserBlocks) {
        for (const [indexKey, layouts] of Object.entries(value.continuationUserBlocks)) {
          set(
            continuationUserBlocksState(
              makeContinuationLayoutId({ sourceBlockId: id, continuationIndex: Number(indexKey) }),
            ),
            layouts,
          );
        }
      }
    },
});

export const globalCustomViewOptions = selector<GlobalCustomViewOptions>({
  key: 'customViewOptions',
  get: ({ get }) => {
    const allocatorConfig = get(openAllocatorSubjectConfig);
    const coverPageImage = get(viewCoverPageImage);
    const custom_footer_text = get(studioCustomFooterText);
    const hide_footer_disclaimer = get(studioHideFooterDisclaimer);
    const hide_footer_pagination = get(studioHideFooterPagination);
    const hide_header_company_name = get(studioHideHeaderViewName);
    const hide_header_logo = get(studioHideHeaderCompanyName);
    const hide_header_view_name = get(studioHideHeaderLogo);
    const images = get(viewImages);
    const pages = get(viewPages);
    const primaryColor = get(viewPrimaryColor);
    const privateAllocatorConfig = get(openPrivateAllocatorConfig);
    const sectionImages = get(sectionPageBackgroundImages);
    const sectionPageImage = get(sectionPageBackgroundImage);
    const studioSectionPageHasFullPageBackground = get(studioSectionPageHasFullPageBackgroundImage);
    const studioSectionPageShouldHideCompanyName = get(studioSectionPageHideCompanyName);
    const studioSectionTextColor = get(viewSectionTextColor);
    const studioTitlePageHasDisclosureTextAlignedToRight = get(studioTitlePageHasDisclosureTextAlignedRight);
    const studioTitlePageHasFullWidthImage = get(studioTitlePageHasFullPageBackgroundImage);
    const studioTitlePageHideLogo = get(studioTitlePageHideCompanyLogo);
    const taxRatesPerSubject = get(viewTaxRatesPerSubject);

    return {
      allocatorConfig,
      coverPageImage,
      custom_footer_text,
      hide_footer_disclaimer,
      hide_footer_pagination,
      hide_header_company_name,
      hide_header_logo,
      hide_header_view_name,
      images,
      pages,
      primaryColor,
      privateAllocatorConfig,
      sectionImages,
      sectionPageImage,
      studioSectionPageHasFullPageBackground,
      studioSectionPageShouldHideCompanyName,
      studioSectionTextColor,
      studioTitlePageHasDisclosureTextAlignedToRight,
      studioTitlePageHasFullWidthImage,
      studioTitlePageHideLogo,
      taxRatesPerSubject,
    };
  },
  set: ({ get, set }, value) => {
    if (value instanceof DefaultValue) {
      set(viewPages, value);
      set(viewCoverPageImage, value);
      set(sectionPageBackgroundImage, value);
      set(viewImages, value);
      set(sectionPageBackgroundImages, value);
      set(studioHideHeaderViewName, value);
      set(studioHideHeaderCompanyName, value);
      set(studioHideHeaderLogo, value);
      set(studioHideFooterPagination, value);
      set(studioHideFooterDisclaimer, value);
      set(studioTitlePageHasFullPageBackgroundImage, value);
      set(studioTitlePageHasDisclosureTextAlignedRight, value);
      set(studioTitlePageHideCompanyLogo, value);
      set(studioSectionPageHasFullPageBackgroundImage, value);
      set(studioSectionPageHideCompanyName, value);
      set(studioCustomFooterText, value);
      set(viewPrimaryColor, value);
      set(viewSectionTextColor, value);
      set(allocatorAnalysisSubject(get(openAllocatorSubject)), value);
      set(openAllocatorSubject, value);
      set(viewTaxRatesPerSubject, value);
      return;
    }
    value.pages && set(viewPages, value.pages);
    value.coverPageImage && set(viewCoverPageImage, value.coverPageImage);
    value.sectionPageImage && set(sectionPageBackgroundImage, value.sectionPageImage);
    set(viewImages, value.images);
    set(sectionPageBackgroundImages, value.sectionImages);
    value.hide_header_company_name && set(studioHideHeaderViewName, value.hide_header_company_name);
    value.hide_header_logo && set(studioHideHeaderCompanyName, value.hide_header_logo);
    value.hide_header_view_name && set(studioHideHeaderLogo, value.hide_header_view_name);
    value.hide_footer_pagination && set(studioHideFooterPagination, value.hide_footer_pagination);
    value.hide_footer_disclaimer && set(studioHideFooterDisclaimer, value.hide_footer_disclaimer);
    value.studioTitlePageHasFullWidthImage &&
      set(studioTitlePageHasFullPageBackgroundImage, value.studioTitlePageHasFullWidthImage);
    value.studioTitlePageHasDisclosureTextAlignedToRight &&
      set(studioTitlePageHasDisclosureTextAlignedRight, value.studioTitlePageHasDisclosureTextAlignedToRight);
    value.studioTitlePageHideLogo && set(studioTitlePageHideCompanyLogo, value.studioTitlePageHideLogo);
    value.studioSectionPageHasFullPageBackground &&
      set(studioSectionPageHasFullPageBackgroundImage, value.studioSectionPageHasFullPageBackground);
    value.studioSectionPageShouldHideCompanyName &&
      set(studioSectionPageHideCompanyName, value.studioSectionPageShouldHideCompanyName);
    value.custom_footer_text && set(studioCustomFooterText, value.custom_footer_text);
    value.primaryColor && set(viewPrimaryColor, value.primaryColor);
    value.studioSectionTextColor && set(viewSectionTextColor, value.studioSectionTextColor);
    value.allocatorConfig && set(openAllocatorSubjectConfig, value.allocatorConfig);
    value.privateAllocatorConfig && set(openPrivateAllocatorConfig, value.privateAllocatorConfig);
    value.taxRatesPerSubject && set(viewTaxRatesPerSubject, value.taxRatesPerSubject);
  },
});
